import { createSlice } from '@reduxjs/toolkit'

import {
  createDomainControl,
  deleteDomainControl,
  getDomainControls,
  getDomainControlSummary,
  getDomainControlSettingForOrg,
  getOrganizationAdminsAndOwner,
  patchDomainControl,
  updateDomainControlSetting,
  verifyDomainControl,
  postDomainControlExport
} from './actions'
import { SLICE_INITIAL_STATE, REPORT_GENERATION_STATUSES } from './constants'

const slice = createSlice({
  name: 'domainControl',
  initialState: SLICE_INITIAL_STATE,
  reducers: {
    setDomain: (state, { payload }) => {
      state.createForm.error = SLICE_INITIAL_STATE.createForm.error
      state.createForm.domain = payload
    },
    setVerificationMethod: (state, { payload }) => {
      state.createForm.verificationMethod = payload
    },
    setError: (state, { payload }) => {
      state.createForm.error = payload
    },
    setExpandedCardDomain: (state, { payload }) => {
      if (!state.list.loadingVerifications[payload.uuid]) {
        state.list.expandedCardDomains = {
          ...state.list.expandedCardDomains,
          [payload.uuid]: payload.value
        }
      }
    },
    setDomainForPopup: (state, { payload }) => {
      state.list.domainForPopup = payload
    },
    setSubmitDisabled: (state, { payload }) => {
      state.createForm.submitDisabled = payload
    },
    openPopup: (state, { payload }) => {
      state.list.openPopupType = payload
      state.list.isPopupOpen = true
    },
    closePopup: (state) => {
      state.list.openPopupType = null
      state.list.isPopupOpen = false
    },
    reset: () => SLICE_INITIAL_STATE
  },
  extraReducers: (builder) => {
    builder.addCase(createDomainControl.pending, (state) => {
      state.createForm.loading = true
      state.createForm.formDisabled = true
    })
    builder.addCase(createDomainControl.fulfilled, (state, { payload }) => {
      state.createForm = SLICE_INITIAL_STATE.createForm
      state.list.entries = [
        payload,
        ...state.list.entries
      ]
      state.list.expandedCardDomains = {
        ...state.list.expandedCardDomains,
        [payload.uuid]: true
      }
    })
    builder.addCase(createDomainControl.rejected, (state, { payload }) => {
      if (payload.type === 'validation') {
        state.createForm.error = payload.payload.message
      }

      state.createForm.submitDisabled = true
      state.createForm.formDisabled = false
      state.createForm.loading = false
    })
    builder.addCase(deleteDomainControl.pending, (state) => {
      state.list.loading = true
    })
    builder.addCase(deleteDomainControl.fulfilled, (state) => {
      const newEntries = [...state.list.entries]
      state.list.entries = newEntries.filter((domain) => domain.uuid !== state.list.domainForPopup.uuid)

      state.list.domainForPopup = SLICE_INITIAL_STATE.list.domainForPopup
      state.list.openPopupType = SLICE_INITIAL_STATE.list.openPopupType
      state.list.isPopupOpen = false
      state.list.loading = false
    })
    builder.addCase(deleteDomainControl.rejected, (state) => {
      state.list.loading = false
    })

    builder.addCase(getDomainControls.pending, (state) => {
      state.list.loading = true
    })
    builder.addCase(getDomainControls.fulfilled, (state, { payload }) => {
      state.list.loading = false
      state.list.entries = payload
    })
    builder.addCase(getDomainControls.rejected, (state) => {
      state.list.loading = false
      state.list.entries = SLICE_INITIAL_STATE.list.entries
    })
    builder.addCase(verifyDomainControl.fulfilled, (state, { payload }) => {
      // Consider updating "state.list.entries" to be a dictionary to avoid
      // having to iterate over the full thing to figure out which one to replace
      const entryIndex = state.list.entries.findIndex((item) => item.uuid === payload.uuid)
      if (entryIndex >= 0) {
        state.list.entries[entryIndex] = payload
      }
      state.list.loadingVerifications = {
        ...state.list.loadingVerifications,
        [payload.uuid]: false
      }
    })
    builder.addCase(verifyDomainControl.pending, (state, { meta }) => {
      const uuid = meta.arg.uuid
      state.list.loadingVerifications = {
        ...state.list.loadingVerifications,
        [uuid]: true
      }
    })
    builder.addCase(verifyDomainControl.rejected, (state, { meta, payload }) => {
      const uuid = meta.arg.uuid
      state.list.loadingVerifications = {
        ...state.list.loadingVerifications,
        [uuid]: false
      }

      if (payload.type === 'validation') {
        state.list.errors = {
          ...state.list.errors,
          [meta.arg.uuid]: payload.payload.message
        }
      }
    })
    builder.addCase(getDomainControlSummary.pending, (state, { meta }) => {
      state.list.allDomainSummaries[meta.arg.uuid] = undefined
    })
    builder.addCase(getDomainControlSummary.fulfilled, (state, { meta, payload }) => {
      state.list.allDomainSummaries[meta.arg.uuid] = payload
    })
    builder.addCase(getDomainControlSummary.rejected, (state, { meta }) => {
      state.list.allDomainSummaries[meta.arg.uuid] = {
        count: 0
      }
    })
    builder.addCase(patchDomainControl.pending, (state) => {
      state.list.loadingActionPopup = true
    })
    builder.addCase(patchDomainControl.rejected, (state) => {
      state.list.loadingActionPopup = false
    })
    builder.addCase(patchDomainControl.fulfilled, (state, { payload }) => {
      state.list.loadingActionPopup = false
      const entryIndex = state.list.entries.findIndex((item) => item.uuid === payload.uuid)
      if (entryIndex > -1) {
        state.list.entries[entryIndex] = payload
      }
      state.list.isPopupOpen = false
      state.list.domainForPopup = null
      state.list.openPopupType = null
    })
    builder.addCase(getDomainControlSettingForOrg.pending, (state) => {
      state.domainControlSetting.loading = true
    })
    builder.addCase(getDomainControlSettingForOrg.fulfilled, (state, { payload }) => {
      state.domainControlSetting.accessOwnerType = payload.access_owner_type
      state.domainControlSetting.accessOwnerValue = payload.access_owner_value
      state.domainControlSetting.hasExistingSetting = true
      state.domainControlSetting.loading = false
    })
    builder.addCase(getDomainControlSettingForOrg.rejected, (state) => {
      state.domainControlSetting.loading = false
    })
    builder.addCase(updateDomainControlSetting.pending, (state) => {
      state.domainControlSetting.loading = true
    })
    builder.addCase(updateDomainControlSetting.fulfilled, (state, { payload }) => {
      state.domainControlSetting.accessOwnerType = payload.access_owner_type
      state.domainControlSetting.accessOwnerValue = payload.access_owner_value
      state.domainControlSetting.hasExistingSetting = true
      state.domainControlSetting.loading = false
    })
    builder.addCase(updateDomainControlSetting.rejected, (state) => {
      state.domainControlSetting.loading = false
    })
    builder.addCase(getOrganizationAdminsAndOwner.pending, (state) => {
      state.domainControlSetting.accessOwnerDropdown.loading = true
    })
    builder.addCase(getOrganizationAdminsAndOwner.fulfilled, (state, { payload }) => {
      state.domainControlSetting.accessOwnerDropdown.options = payload.results
      state.domainControlSetting.accessOwnerDropdown.loading = false
    })
    builder.addCase(getOrganizationAdminsAndOwner.rejected, (state) => {
      state.domainControlSetting.accessOwnerDropdown = SLICE_INITIAL_STATE.domainControlSetting.accessOwnerDropdown
    })
    builder.addCase(postDomainControlExport.pending, (state, { meta }) => {
      state.list.reportGeneration[meta.arg.uuid] = REPORT_GENERATION_STATUSES.pending
    })
    builder.addCase(postDomainControlExport.fulfilled, (state, { meta }) => {
      state.list.reportGeneration[meta.arg.uuid] = REPORT_GENERATION_STATUSES.generated
    })
    builder.addCase(postDomainControlExport.rejected, (state, { meta }) => {
      delete state.list.reportGeneration[meta.arg.uuid]
    })
  }
})

export const { reducer } = slice
export const {
  closePopup,
  openPopup,
  reset,
  setDomain,
  setDomainForPopup,
  setError,
  setExpandedCardDomain,
  setSubmitDisabled,
  setVerificationMethod,
} = slice.actions
